/** @jsxImportSource preact */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { WAIVER_REASON_LABELS } from './fraud-settings-api'

/**
 * The live waivers as a compact table. This screen sits in the narrow Front sidebar, so each row is
 * three short columns and the long part, the note, opens in a sheet instead of stretching the row.
 */

export interface Waiver {
  id: number
  bookingReference: string
  reasonCode: string
  reasonNote: string | null
  requireZeroBalance: boolean
  waivedBy: string
  approvedBy: string | null
  created: string
}

/** Short enough for a sidebar column. The full labels are in the sheet. */
const SHORT_REASON: Record<string, string> = {
  balance_cleared: 'Balance cleared',
  hardship_plan_agreed: 'Hardship plan',
  default_disputed_resolved: 'Dispute resolved',
  system_error: 'System error',
  migrated_from_sheet: 'Old sheet',
}

const PAGE_SIZE = 25

/** What a search looks at. The note carries the customer's email for imported rows, which is what analysts search by. */
const searchText = (waiver: Waiver) =>
  [
    waiver.bookingReference,
    waiver.reasonNote ?? '',
    waiver.waivedBy,
    waiver.approvedBy ?? '',
    WAIVER_REASON_LABELS[waiver.reasonCode] ?? waiver.reasonCode,
  ]
    .join(' ')
    .toLowerCase()

const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000))
const agoLabel = (iso: string) => {
  const days = daysSince(iso)
  return days <= 0 ? 'Today' : `${days}d ago`
}

function NotesSheet({
  waiver,
  switchedOff,
  busy,
  onClose,
  onRevoke,
}: {
  waiver: Waiver
  switchedOff: boolean
  busy: boolean
  onClose: () => void
  onRevoke: () => void
}) {
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // The note is stored as one line, with " — " between the pieces (sheet email, the team's note,
  // who imported it). Split them so each reads on its own line.
  const noteParts = (waiver.reasonNote ?? '')
    .split(' — ')
    .map(part => part.trim())
    .filter(Boolean)

  const row = 'grid grid-cols-[6.5rem_1fr] gap-2 py-1.5 border-b border-gray-100 text-sm'

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="waiver-notes-title"
        className="w-full sm:max-w-md max-h-[85vh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg p-4 shadow-xl"
        onClick={event => event.stopPropagation()}
      >
        <h3
          id="waiver-notes-title"
          className="font-mono font-semibold text-gray-800 mb-2"
        >
          {waiver.bookingReference}
        </h3>

        <dl>
          <div className={row}>
            <dt className="text-gray-500">Reason</dt>
            <dd className="text-gray-800">{WAIVER_REASON_LABELS[waiver.reasonCode] ?? waiver.reasonCode}</dd>
          </div>
          <div className={row}>
            <dt className="text-gray-500">Waived by</dt>
            <dd className="text-gray-800 break-words">{waiver.waivedBy}</dd>
          </div>
          <div className={row}>
            <dt className="text-gray-500">When</dt>
            <dd className="text-gray-800">
              {new Date(waiver.created).toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}{' '}
              ({agoLabel(waiver.created).toLowerCase()})
            </dd>
          </div>
          {waiver.approvedBy && (
            <div className={row}>
              <dt className="text-gray-500">Approved by</dt>
              <dd className="text-gray-800 break-words">{waiver.approvedBy}</dd>
            </div>
          )}
          <div className={row}>
            <dt className="text-gray-500">Balance</dt>
            <dd className="text-gray-800">
              {waiver.requireZeroBalance ? 'Waits for the balance to clear' : 'No balance condition'}
            </dd>
          </div>
          {switchedOff && (
            <div className={row}>
              <dt className="text-gray-500">In effect</dt>
              <dd className="text-amber-700">Not while waivers are switched off</dd>
            </div>
          )}
        </dl>

        <h4 className="mt-3 mb-1 text-sm font-medium text-gray-700">Notes</h4>
        {noteParts.length === 0 ? (
          <p className="text-sm text-gray-500">No note was recorded.</p>
        ) : (
          noteParts.map((part, index) => (
            <p
              key={index}
              className="text-sm text-gray-800 break-words mb-1"
            >
              {part}
            </p>
          ))
        )}

        <div className="mt-4 flex justify-between gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onRevoke}
            className="px-3 py-2 text-sm text-red-600 hover:text-red-800 disabled:text-gray-400"
          >
            Revoke
          </button>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-700 text-white rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export function FraudWaiversTable({
  waivers,
  switchedOff,
  busy,
  onRevoke,
}: {
  waivers: Waiver[]
  switchedOff: boolean
  busy: boolean
  onRevoke: (bookingReference: string) => void
}) {
  const [openId, setOpenId] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const [shown, setShown] = useState(PAGE_SIZE)
  const open = waivers.find(waiver => waiver.id === openId)

  const needle = query.trim().toLowerCase()
  const matches = useMemo(
    () => (needle ? waivers.filter(waiver => searchText(waiver).includes(needle)) : waivers),
    [waivers, needle],
  )
  // A search shows every match: paging a short result list would hide the one being looked for.
  const visible = needle ? matches : matches.slice(0, shown)
  const remaining = matches.length - visible.length

  // The columns that do not fit a narrow sidebar come back from the `sm` width up, which is the
  // width of the frame the screen is in, not of the device.
  const extra = 'hidden sm:table-cell'

  return (
    <>
      <div className="relative">
        <label
          htmlFor="waiver-search"
          className="sr-only"
        >
          Search waivers
        </label>
        <input
          id="waiver-search"
          type="text"
          inputMode="search"
          enterKeyHint="search"
          value={query}
          placeholder="Search booking or email"
          autoComplete="off"
          onInput={event => setQuery((event.target as HTMLInputElement).value)}
          onKeyDown={event => {
            if (event.key === 'Escape') setQuery('')
          }}
          className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear the search"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-800"
          >
            ✕
          </button>
        )}
      </div>
      <p
        className="mt-1 mb-2 text-xs text-gray-500"
        aria-live="polite"
      >
        {needle
          ? `${matches.length} of ${waivers.length} waivers`
          : `${waivers.length} waiver${waivers.length === 1 ? '' : 's'}`}
      </p>

      {matches.length === 0 ? (
        <p className="text-sm text-gray-600">
          No waiver matches &ldquo;{query.trim()}&rdquo;. This only searches waivers that are in place. Clear the search
          to see them all.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">Active waivers</caption>
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                <th
                  scope="col"
                  className="py-2 pr-2 font-medium"
                >
                  Booking
                </th>
                <th
                  scope="col"
                  className={`py-2 pr-2 font-medium ${extra}`}
                >
                  Reason
                </th>
                <th
                  scope="col"
                  className="py-2 pr-2 font-medium"
                >
                  Waived
                </th>
                <th
                  scope="col"
                  className={`py-2 pr-2 font-medium ${extra}`}
                >
                  By
                </th>
                <th
                  scope="col"
                  className="py-2 text-right font-medium"
                >
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map(waiver => (
                <tr
                  key={waiver.id}
                  className="border-b border-gray-100 align-top"
                >
                  <td className="py-2 pr-2">
                    <div className="font-mono text-[13px] font-semibold text-gray-800">{waiver.bookingReference}</div>
                    <div className="text-xs text-gray-500 sm:hidden">
                      {SHORT_REASON[waiver.reasonCode] ?? waiver.reasonCode}
                    </div>
                  </td>
                  <td className={`py-2 pr-2 text-gray-700 ${extra}`}>
                    {SHORT_REASON[waiver.reasonCode] ?? waiver.reasonCode}
                    {waiver.requireZeroBalance && <span className="text-xs text-gray-500"> · waits for balance</span>}
                  </td>
                  <td className="py-2 pr-2">
                    <div className="text-gray-800 whitespace-nowrap">{agoLabel(waiver.created)}</div>
                    <div
                      className="text-xs text-gray-500 truncate max-w-[5.5rem] sm:hidden"
                      title={waiver.waivedBy}
                    >
                      {waiver.waivedBy}
                    </div>
                  </td>
                  <td className={`py-2 pr-2 text-gray-700 break-all ${extra}`}>{waiver.waivedBy}</td>
                  <td className="py-2 text-right">
                    <div className="flex flex-col items-end gap-1 sm:flex-row sm:justify-end sm:gap-4">
                      <button
                        type="button"
                        onClick={() => setOpenId(waiver.id)}
                        className="p-0 text-indigo-600 hover:text-indigo-800 underline"
                        aria-label={`Notes for ${waiver.bookingReference}`}
                      >
                        Notes
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onRevoke(waiver.bookingReference)}
                        className="p-0 text-red-600 hover:text-red-800 disabled:text-gray-400"
                        aria-label={`Revoke the waiver on ${waiver.bookingReference}`}
                      >
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {remaining > 0 && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => setShown(count => count + PAGE_SIZE)}
            className="px-4 py-2 text-sm text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-50"
          >
            Show {Math.min(PAGE_SIZE, remaining)} more
          </button>
        </div>
      )}

      {open && (
        <NotesSheet
          waiver={open}
          switchedOff={switchedOff}
          busy={busy}
          onClose={() => setOpenId(null)}
          onRevoke={() => {
            setOpenId(null)
            onRevoke(open.bookingReference)
          }}
        />
      )}
    </>
  )
}
